import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../lib/api';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import {
    ArrowRight, Phone, MessageCircle, Mail, ChevronDown, Check, X, AlertTriangle,
    Users, Wallet, ListChecks, BarChart3, Smartphone, ShieldCheck,
    NotebookText, CircleDollarSign, MapPin, FileWarning, RotateCw, UsersRound,
    Car, Building2, Calculator, Wrench, Store, Apple,
    Shield, Cloud, Scale, Headphones, Sparkles, Send, Loader2, Star, Download
} from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

/* ═══════════ STORE BADGES — App Store + Google Play ═══════════ */
const STORE_LINKS = {
    appStore: 'https://apps.apple.com/tr/app/mactech',     // TODO: gerçek link
    playStore: 'https://play.google.com/store/apps/details?id=tr.mactech.app', // TODO: gerçek link
};

const StoreBadges = ({ size = 'md', testIdPrefix = 'store' }) => {
    const sizes = {
        sm: { padding: 'px-3 py-2', icon: 'w-5 h-5', label: 'text-[9px]', store: 'text-xs' },
        md: { padding: 'px-4 py-2.5', icon: 'w-6 h-6', label: 'text-[10px]', store: 'text-sm' },
        lg: { padding: 'px-5 py-3', icon: 'w-7 h-7', label: 'text-[11px]', store: 'text-base' },
    };
    const s = sizes[size] || sizes.md;
    return (
        <div className="flex flex-wrap gap-2.5">
            <a href={STORE_LINKS.appStore} target="_blank" rel="noopener noreferrer"
                className={`${s.padding} flex items-center gap-2.5 rounded-xl bg-black text-white border border-[#C5A267]/30 hover:border-[#C5A267] hover:scale-[1.02] transition-all`}
                data-testid={`${testIdPrefix}-appstore`}>
                <Apple className={s.icon} />
                <div className="flex flex-col leading-tight">
                    <span className={`${s.label} text-gray-400 uppercase tracking-wide`}>İndir</span>
                    <span className={`${s.store} font-semibold`}>App Store</span>
                </div>
            </a>
            <a href={STORE_LINKS.playStore} target="_blank" rel="noopener noreferrer"
                className={`${s.padding} flex items-center gap-2.5 rounded-xl bg-black text-white border border-[#C5A267]/30 hover:border-[#C5A267] hover:scale-[1.02] transition-all`}
                data-testid={`${testIdPrefix}-playstore`}>
                <svg className={s.icon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zM15.5 10.5l2.546 2.547-2.93 1.694L13.5 12.5l2-2zm-7.93-6.715l10.937 6.333-2.302 2.302L7.57 3.785z" />
                </svg>
                <div className="flex flex-col leading-tight">
                    <span className={`${s.label} text-gray-400 uppercase tracking-wide`}>İndir</span>
                    <span className={`${s.store} font-semibold`}>Google Play</span>
                </div>
            </a>
        </div>
    );
};

/* ═══════════ HERO MOCKUP — Gerçek OTO-CARİ ekran görüntüleri ═══════════ */
const HERO_DESKTOP_IMG = 'https://customer-assets.emergentagent.com/job_7d76f736-cbcd-459a-b03f-cd28dd950dc8/artifacts/so6aqbrl_image.png';
const HERO_MOBILE_IMG = 'https://customer-assets.emergentagent.com/job_7d76f736-cbcd-459a-b03f-cd28dd950dc8/artifacts/6ybeww7b_WhatsApp%20Image%202026-06-13%20at%2017.03.17.jpeg';

const HeroMockup = ({ isDark }) => {
    const surface = isDark ? 'bg-[#0E0E0E] border-[#C5A267]/30' : 'bg-white border-[#C5A267]/40';
    const dimC = isDark ? 'text-gray-500' : 'text-gray-400';

    return (
        <div className="relative w-full max-w-2xl mx-auto" data-testid="hero-mockup">
            {/* Glow */}
            <div className="absolute -inset-10 bg-[#C5A267]/15 blur-3xl rounded-[3rem] -z-10" />

            {/* Browser frame with real desktop screenshot */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
                className={`${surface} border-2 rounded-2xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(197,162,103,0.35)]`}>
                {/* Browser top bar */}
                <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${isDark ? 'border-[#2a2a2a] bg-[#0a0a0a]' : 'border-gray-200 bg-gray-100'}`}>
                    <div className="flex gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                        <div className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                    </div>
                    <div className={`flex-1 text-center text-[10px] font-mono ${dimC}`}>app.mactech.tr / oto-cari</div>
                </div>

                {/* Real screenshot */}
                <img
                    src={HERO_DESKTOP_IMG}
                    alt="MacTech OTO-CARİ — Genel Bakış ekranı"
                    className="w-full h-auto block"
                    loading="eager"
                    decoding="async"
                    data-testid="hero-desktop-screenshot"
                />
            </motion.div>

            {/* Floating phone mockup with real mobile screenshot */}
            <motion.div initial={{ opacity: 0, x: 30, y: 10 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
                className="hidden md:block absolute -right-8 -bottom-10 w-40 lg:w-44">
                <div className={`${surface} border-[3px] rounded-[2rem] p-1.5 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.6)]`}>
                    <div className="rounded-[1.6rem] overflow-hidden bg-black">
                        <img
                            src={HERO_MOBILE_IMG}
                            alt="MacTech OTO-CARİ — Mobil uygulama"
                            className="w-full h-auto block"
                            loading="eager"
                            decoding="async"
                            data-testid="hero-mobile-screenshot"
                        />
                    </div>
                </div>
                {/* Phone notch detail */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-black/70 rounded-full" />
            </motion.div>

            {/* "Canlı" pulse badge */}
            <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1, duration: 0.4 }}
                className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/90 backdrop-blur-sm border border-emerald-300/30 shadow-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                <span className="text-[10px] font-bold text-white">CANLI</span>
            </motion.div>
        </div>
    );
};

/* ═══════════ PROBLEM DATA ═══════════ */
const PROBLEMS = [
    { icon: NotebookText, title: 'Müşteri bilgileri kaybolur', desc: '"Geçen ay arayan müşterinin numarası neredeydi?" diye defterleri karıştırırsınız. Notlar telefonda, isimler ajandada.' },
    { icon: CircleDollarSign, title: '"Bana borçlu olan kimdi?"', desc: 'Ahmet ustadan 8.500 lira alacaktınız. Mehmet beye 3.000 verecektiniz. Ay sonu geliyor, kafanız karışıyor.' },
    { icon: MapPin, title: 'Dışarıdayken bilgisiz kalırsınız', desc: 'Müşteri arıyor: "O araç hâlâ duruyor mu?" Siz dışarıdasınız, çalışan da bilmiyor. Müşteri kaçar.' },
    { icon: FileWarning, title: 'Ay sonu hesabı bir karmaşa', desc: 'Bu ay kâr mı ettiniz, zarar mı? Excel açıyorsunuz, sayılar tutmuyor. Vergici geliyor, stres başlıyor.' },
    { icon: RotateCw, title: 'Aynı işi her gün tekrar yaparsınız', desc: 'Aynı SMS, aynı hatırlatma, aynı liste — her gün elden yazarsınız. Zamanınız buharlaşır.' },
    { icon: UsersRound, title: 'Çalışanlarınız ne yaptı?', desc: 'Yusuf sabah ne yaptı? Servisteki araç bitti mi? Kim hangi müşteriyle ilgilendi? Net cevap yok.' },
];

/* ═══════════ FEATURES DATA ═══════════ */
const FEATURES = [
    {
        icon: Users,
        title: 'Müşteri Takibi',
        head: 'Hangi müşteri kim, ne aldı, ne istedi — hepsi tek kartta.',
        body: 'Müşterinin adı, telefonu, geçmiş alışverişleri, notları, borcu, son aramanız — hepsi tek bir sayfada.',
        benefit: 'Aradığında "Sayın Ahmet Bey, geçen sefer şu aracı bakmıştınız" diyebilirsiniz.',
    },
    {
        icon: Wallet,
        title: 'Gelir Gider Takibi',
        head: 'Para nereden geliyor, nereye gidiyor — anında görün.',
        body: 'Her satışınızı, her ödemenizi, her borcunuzu yazın. Sistem sizin için hesaplar, ay sonunu beklemezsiniz.',
        benefit: 'Vergici geldiğinde stresiniz olmaz. Kararlarınızı gerçek sayılarla verirsiniz.',
    },
    {
        icon: ListChecks,
        title: 'İş Takibi',
        head: 'Hangi iş kimde, ne aşamada — net görün.',
        body: '"Bugün servise gelen aracın işi bitti mi?" sorularına tek bakışta cevap. Görevleri yazın, takip edilir.',
        benefit: 'Hiçbir iş unutulmaz. Çalışanlarınız işlerini bilir.',
    },
    {
        icon: BarChart3,
        title: 'İşletmenizin Durumu',
        head: 'Bu ay kaç müşteri geldi, ne kazandınız — bir bakışta.',
        body: 'Sabah açtığınızda küçük bir özet sayfası: "Dün 4 müşteri, 12.500 lira ciro, 3 borç ödendi."',
        benefit: 'Karar verirken tahminle değil, gerçek bilgiyle hareket edersiniz.',
    },
    {
        icon: Smartphone,
        title: 'Telefonunuzdan Yönetin',
        head: 'Dükkanda olmasanız bile her şey cebinizde.',
        body: 'iPhone veya Android telefonunuza yüklersiniz. Evde, sahada, başka şehirde — her yerden bakarsınız.',
        benefit: 'Tatildeyken bile dükkanı yönetebilirsiniz.',
    },
    {
        icon: ShieldCheck,
        title: 'Bilgileriniz Güvende',
        head: 'Bilgisayar bozulsa, telefon kaybolsa — bilgiler size kalır.',
        body: 'Tüm bilgileriniz bizim bilgisayarlarımızda saklanır. Saatte bir otomatik yedek alınır.',
        benefit: '"Excel kayboldu, ne yapacağım?" diyerek panik yapmazsınız.',
    },
];

/* ═══════════ SECTOR DATA ═══════════ */
const SECTORS = [
    {
        key: 'galeri', icon: Car, label: 'Oto Galeriler', active: true,
        pain: '"Hangi araç hangi sahipte, hangisi konsinye, hangisinde ne kadar kâr var — hatırlamak imkansız."',
        solutions: [
            'Her aracı tek kartta saklayın (plaka, fiyat, fotoğraf, alış-satış tarihi)',
            'Konsinye araçları ayrı takip edin',
            'Müşteri "gelecek hafta bakacağım" derse, 3 gün sonra hatırlatır',
            'Her aracın size ne kazandırdığı ayrı görünür',
        ],
        result: 'Galericiler ortalama 3 kat daha hızlı araç satıyor.',
        cta: 'Galerim İçin Demo İstiyorum',
    },
    {
        key: 'emlak', icon: Building2, label: 'Emlakçılar', active: false,
        pain: '"Hangi daire kimde, hangisi satıldı, hangi müşteri ne arıyor — kafam karıştı."',
        solutions: [
            'Her gayrimenkulün ayrı kartı',
            'Müşterinin aradığı kriterlere göre otomatik eşleştirme',
            'Randevular tek takvimde',
            'İlanlarınız otomatik güncellensin',
        ],
        result: 'Yakında — Q2 2026',
        cta: 'Hazır Olduğunda Bana Haber Ver',
    },
    {
        key: 'muhasebe', icon: Calculator, label: 'Muhasebeciler', active: false,
        pain: '"30 firma, her birinin ayrı klasörü, ayrı Excel\'i — yetiştiremiyorum."',
        solutions: [
            'Her firma için ayrı sayfa',
            'Belgeler ortak alanda',
            'Hatırlatmalar otomatik',
            'Vergi dönemlerinde uyarı sistemi',
        ],
        result: 'Yol haritamızda.',
        cta: 'Bana Haber Verin',
    },
    {
        key: 'servis', icon: Wrench, label: 'Servisler', active: false,
        pain: '"Hangi müşterinin işi nerede, parça geldi mi, ücret ne kadar tutuyor?"',
        solutions: [
            'Her servis kaydı ayrı (araç, müşteri, yapılan iş)',
            'Parça stoğunuz görünür',
            'Müşteri arayınca: "İşiniz %80 bitti" dersiniz',
            'Randevu sistemi',
        ],
        result: 'Yakında geliyor.',
        cta: 'Servisim İçin İlgileniyorum',
    },
    {
        key: 'esnaf', icon: Store, label: 'Diğer Esnaflar', active: true,
        pain: '"Mağazam küçük, ama defterler büyüyor."',
        solutions: [
            'Her müşterinizi yazın',
            'Her satışı kaydedin',
            'Ay sonu özetinizi alın',
            'Telefonunuzdan yönetin',
        ],
        result: 'Esnafa uygun, sade arayüz.',
        cta: 'Esnafıma Uygun mu? Konuşalım',
    },
];

/* ═══════════ COMPARISON DATA ═══════════ */
const COMPARISON = [
    { label: 'Bilgilerim güvende mi?', defter: { v: 'Bilgisayar bozulursa biter', t: 'warn' }, abroad: { v: 'Güvende', t: 'ok' }, mactech: { v: 'Güvende + yasaya uygun', t: 'best' } },
    { label: 'Telefonumdan kullanabilir miyim?', defter: { v: 'Hayır', t: 'bad' }, abroad: { v: 'Var ama Türkçe değil', t: 'warn' }, mactech: { v: 'Türkçe, kolay', t: 'best' } },
    { label: 'Türkçe destek var mı?', defter: { v: '—', t: 'neutral' }, abroad: { v: 'İngilizce', t: 'bad' }, mactech: { v: '7/24 Türkçe', t: 'best' } },
    { label: 'Devletin istediği bilgiler hazır mı?', defter: { v: 'El ile uğraşmalı', t: 'bad' }, abroad: { v: 'Eksik', t: 'warn' }, mactech: { v: 'KVKK\'ya uygun', t: 'best' } },
    { label: 'Hemen kullanabilir miyim?', defter: { v: '—', t: 'neutral' }, abroad: { v: 'Kurulum bekler', t: 'warn' }, mactech: { v: 'Hesap açar açmaz', t: 'best' } },
    { label: 'Ayda kaça mal olur?', defter: { v: 'Gizli kayıplar', t: 'warn' }, abroad: { v: '2.500 TL+', t: 'warn' }, mactech: { v: '499 TL\'den', t: 'best' } },
    { label: 'Sektörüme özel mi?', defter: { v: 'Hayır', t: 'bad' }, abroad: { v: 'Genel kullanım', t: 'warn' }, mactech: { v: 'Galeri, emlak, vs.', t: 'best' } },
];

const CellIcon = ({ t }) => {
    if (t === 'best' || t === 'ok') return <Check className="w-4 h-4 text-emerald-400" />;
    if (t === 'warn') return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    if (t === 'bad') return <X className="w-4 h-4 text-rose-400" />;
    return <span className="text-gray-500">—</span>;
};

/* ═══════════ TRUST DATA ═══════════ */
const TRUST_ITEMS = [
    { icon: Shield, title: 'Şifrelenmiş Bağlantı', desc: 'Tarayıcınızla bizim aramızda banka düzeyinde şifre var. Kimse aradan bilgi alamaz.' },
    { icon: Cloud, title: 'Saatlik Yedek', desc: 'Her saat başı bilgileriniz otomatik yedeklenir. Bir kayıp olursa hemen geri getirilir.' },
    { icon: Scale, title: 'Yasaya Uygun', desc: 'Devletin istediği KVKK kurallarına tam uyuyoruz. Müşteri bilgileriniz yasayla korunmuş.' },
    { icon: Headphones, title: 'Pazartesi Sabah Aranır', desc: 'Sorun yaşarsanız Türkçe ekibimiz hep yanınızda. WhatsApp\'tan da yazabilirsiniz.' },
];

/* ═══════════ FAQ DATA ═══════════ */
const FAQ_ITEMS = [
    { q: 'Para iade var mı?', a: '14 gün boyunca tamamen ücretsiz denersiniz. Beğenmezseniz tek kuruş ödemezsiniz. Kart bilgisi de istemiyoruz.' },
    { q: 'Mevcut bilgilerimi nasıl aktarırım?', a: 'Excel veya defter olarak gönderirsiniz. Ekibimiz sizin için sisteme yükler. Ücretsiz.' },
    { q: 'Bilgisayarım yok, sadece telefonum var. Olur mu?', a: 'Olur. iPhone veya Android telefonunuza uygulamayı yükler, sıfır bilgisayar ile yönetirsiniz.' },
    { q: 'Çalışanlarım da kullanabilir mi?', a: 'Evet. Her çalışanınız için ayrı şifre verirsiniz. Kim ne yaptı görürsünüz.' },
    { q: 'Eğitim verir misiniz?', a: 'Hesabınızı açtığınızda 1 saat boyunca telefondan veya görüntülü görüşmeyle size sistemi anlatırız. Ücretsiz.' },
    { q: 'İptal etmek istersem?', a: 'İstediğiniz an iptal edersiniz. Bilgilerinizi 90 gün boyunca size indirme imkanı sunarız.' },
    { q: 'Sektörüm listede yok, ne yapayım?', a: 'Bizi arayın, size özel hazırlayabiliriz. 0530 848 78 36.' },
];

/* ═══════════ DEMO FORM ═══════════ */
const DemoForm = ({ isDark }) => {
    const [form, setForm] = useState({ name: '', phone: '', email: '', request_type: 'Galerici', message: '', kvkk_consent: false });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const cardBg = isDark ? 'bg-slate-900/40 border-slate-800' : 'bg-white border-gray-200';
    const heading = isDark ? 'text-white' : 'text-gray-900';

    const submit = async (e) => {
        e.preventDefault();
        if (form.name.trim().length < 2) { toast.error('Adınızı yazın'); return; }
        if (!form.phone.trim()) { toast.error('Telefonunuzu yazın'); return; }
        if (!form.kvkk_consent) { toast.error('KVKK onayı vermeniz gerekiyor'); return; }
        setSubmitting(true);
        try {
            await api.submitLead({ ...form, app_slug: 'galeri', source: 'home_landing' });
            setDone(true);
            toast.success('Talebiniz alındı! Yarım saat içinde sizi arıyoruz.');
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Form gönderilemedi';
            toast.error(typeof msg === 'string' ? msg : 'Hata oluştu');
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className={`${cardBg} border rounded-2xl p-10 text-center`} data-testid="demo-form-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#C5A267]/20 border border-[#C5A267]/40 flex items-center justify-center mb-4">
                    <Check className="w-8 h-8 text-[#C5A267]" />
                </div>
                <h3 className={`text-2xl font-bold ${heading} mb-2`}>Teşekkürler!</h3>
                <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                    Talebiniz alındı. Yarım saat içinde sizi arıyoruz.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className={`${cardBg} border rounded-2xl p-6 sm:p-8 space-y-4`} data-testid="home-demo-form">
            <div>
                <label className={`block text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-1.5`}>Adınız Soyadınız</label>
                <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Örn: Ahmet Yılmaz"
                    className="bg-transparent border-[#C5A267]/20 focus:border-[#C5A267]" data-testid="home-name-input" />
            </div>
            <div>
                <label className={`block text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-1.5`}>Telefon Numaranız</label>
                <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="05XX XXX XX XX"
                    className="bg-transparent border-[#C5A267]/20 focus:border-[#C5A267]" data-testid="home-phone-input" />
            </div>
            <div>
                <label className={`block text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-1.5`}>Mesleğiniz</label>
                <Select value={form.request_type} onValueChange={(v) => setForm(f => ({ ...f, request_type: v }))}>
                    <SelectTrigger className="bg-transparent border-[#C5A267]/20" data-testid="home-job-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="Galerici">Galerici</SelectItem>
                        <SelectItem value="Emlakçı">Emlakçı</SelectItem>
                        <SelectItem value="Muhasebeci">Muhasebeci</SelectItem>
                        <SelectItem value="Servis Sahibi">Servis Sahibi</SelectItem>
                        <SelectItem value="Esnaf / Diğer">Esnaf / Diğer</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div>
                <label className={`block text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-1.5`}>Ne soracaksanız yazın <span className="text-[#C5A267]/60">(isteğe bağlı)</span></label>
                <Textarea value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Örn: 25 araçlık galerim için sistem arıyorum."
                    rows={3} className="bg-transparent border-[#C5A267]/20 focus:border-[#C5A267]" data-testid="home-message-textarea" />
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-[#C5A267]/5 border border-[#C5A267]/20">
                <Checkbox id="home-kvkk" checked={form.kvkk_consent} onCheckedChange={(v) => setForm(f => ({ ...f, kvkk_consent: !!v }))}
                    className="mt-0.5 data-[state=checked]:bg-[#C5A267] data-[state=checked]:border-[#C5A267]" data-testid="home-kvkk-checkbox" />
                <label htmlFor="home-kvkk" className={`text-xs leading-relaxed cursor-pointer ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Beni arayabilirsiniz. Bilgilerimin KVKK kapsamında işlenmesini kabul ediyorum.
                </label>
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-b from-[#C5A267] to-[#B59257] hover:from-[#D4B98D] hover:to-[#C5A267] text-black font-bold py-6 text-base rounded-xl shadow-[0_4px_24px_-8px_rgba(197,162,103,0.6)]"
                data-testid="home-submit-btn">
                {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
                Beni Arasınlar
            </Button>
            <p className={`text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                ⏱ Ortalama yarım saat içinde arıyoruz
            </p>
        </form>
    );
};

/* ═══════════ MAIN HOME PAGE ═══════════ */
const Home = () => {
    const { isAuthenticated } = useAuth();
    const { isDark } = useTheme();
    const [openFaq, setOpenFaq] = useState(0);
    const [activeSector, setActiveSector] = useState('galeri');
    const [reviewStats, setReviewStats] = useState({ avg_rating: 0, total: 0 });

    useEffect(() => {
        api.getReviewStats().then(setReviewStats).catch(() => {});
    }, []);

    const heading = isDark ? 'text-white' : 'text-gray-900';
    const subhead = isDark ? 'text-gray-300' : 'text-gray-600';
    const muted = isDark ? 'text-gray-400' : 'text-gray-500';
    const sectionAlt = isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50';
    const cardBase = isDark ? 'bg-[#0E0E0E]/60 border-[#C5A267]/15' : 'bg-white border-gray-200';

    const sector = SECTORS.find(s => s.key === activeSector) || SECTORS[0];

    return (
        <div className="min-h-screen relative" data-testid="home-page">
            <SEO
                path="/"
                title="İşletmenizi Tek Yerden Yönetin — MacTech"
                description="Müşteri takibi, gelir gider takibi, iş takibi — hepsi tek ekranda. Galeri, emlak, muhasebe ve esnaf için sade Türkçe sistem. 14 gün ücretsiz deneyin."
                keywords="müşteri takibi, galeri yazılımı, esnaf programı, iş takibi, gelir gider takibi, oto galeri yönetimi, emlak programı"
            />

            {/* ═══════ HERO ═══════ */}
            <section className="relative pt-28 pb-16 px-4 overflow-hidden" data-testid="hero-section">
                {/* Background ornament */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-[#C5A267]/8 rounded-full blur-3xl" />
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#C5A267]/5 rounded-full blur-3xl" />
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* LEFT: Copy */}
                        <motion.div initial="hidden" animate="visible" variants={stagger}>
                            <motion.div variants={fadeUp}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C5A267]/10 border border-[#C5A267]/30 mb-6">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#C5A267] animate-pulse" />
                                <span className="text-xs font-semibold text-[#C5A267] tracking-wide uppercase">Esnaflar ve İşletme Sahipleri İçin</span>
                            </motion.div>

                            <motion.h1 variants={fadeUp}
                                className={`text-4xl sm:text-5xl lg:text-6xl font-black ${heading} leading-[1.05] tracking-tight mb-6`}>
                                İşletmenizi <span className="text-[#C5A267]">tek yerden</span> yönetin.
                            </motion.h1>

                            <motion.p variants={fadeUp} className={`text-lg ${subhead} leading-relaxed mb-8 max-w-xl`}>
                                Müşterilerinizi unutmayın, paranızı kaybetmeyin, işlerinizi geriden takip etmeyin.
                                <br />
                                <span className={heading + ' font-semibold'}>Hepsi tek ekranda, telefonunuzdan bile.</span>
                            </motion.p>

                            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3 mb-6">
                                <Link to={isAuthenticated ? '/dashboard' : '/kayit'}>
                                    <Button className="bg-gradient-to-b from-[#C5A267] to-[#B59257] hover:from-[#D4B98D] hover:to-[#C5A267] text-black font-bold px-8 py-6 text-base rounded-xl shadow-[0_4px_24px_-8px_rgba(197,162,103,0.5)] hover:shadow-[0_8px_32px_-8px_rgba(197,162,103,0.7)] hover:-translate-y-0.5 transition-all"
                                        data-testid="hero-primary-cta">
                                        14 Gün Ücretsiz Deneyin <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                                <a href="#demo-form">
                                    <Button variant="outline" className="border-[#C5A267]/40 text-[#C5A267] hover:bg-[#C5A267]/10 hover:border-[#C5A267] px-8 py-6 text-base rounded-xl"
                                        data-testid="hero-secondary-cta">
                                        Bize Ulaşın, Anlatalım
                                    </Button>
                                </a>
                            </motion.div>

                            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm mb-6">
                                {['Kart bilgisi istemiyoruz', 'Hemen kullanmaya başlayın', 'iOS ve Android uygulaması var'].map((t) => (
                                    <div key={t} className={`flex items-center gap-1.5 ${muted}`}>
                                        <Check className="w-4 h-4 text-[#C5A267]" /> <span>{t}</span>
                                    </div>
                                ))}
                            </motion.div>

                            {/* Store badges */}
                            <motion.div variants={fadeUp}>
                                <StoreBadges size="md" testIdPrefix="hero-store" />
                            </motion.div>
                        </motion.div>

                        {/* RIGHT: Mockup */}
                        <div className="relative">
                            <HeroMockup isDark={isDark} />
                        </div>
                    </div>

                    {/* Sector strip — only ACTIVE sectors prominent */}
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
                        className="mt-16 pt-8 border-t border-[#C5A267]/15">
                        <p className={`text-center text-xs uppercase tracking-[0.2em] ${muted} mb-4`}>
                            Şu işletmeler tarafından kullanılıyor
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
                            {SECTORS.filter(s => s.active).map((s) => {
                                const Icon = s.icon;
                                return (
                                    <div key={s.key} className={`flex items-center gap-2 ${heading}`}>
                                        <Icon className="w-5 h-5 text-[#C5A267]" />
                                        <span className="text-base font-semibold">{s.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ═══════ PROBLEM ═══════ */}
            <section className={`${sectionAlt} py-20 px-4 border-y border-[#C5A267]/10`} data-testid="problems-section">
                <div className="max-w-6xl mx-auto">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
                        className="text-center max-w-2xl mx-auto mb-12">
                        <motion.h2 variants={fadeUp} className={`text-3xl sm:text-4xl font-black ${heading} tracking-tight mb-4`}>
                            Tanıdık geldi mi?
                        </motion.h2>
                        <motion.p variants={fadeUp} className={`${subhead}`}>
                            Çoğu işletme sahibi bu sıkıntıları her gün yaşıyor.
                        </motion.p>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {PROBLEMS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <motion.div key={i} variants={fadeUp}
                                    className={`${cardBase} border rounded-xl p-6 hover:border-[#C5A267]/35 hover:-translate-y-0.5 transition-all`}
                                    data-testid={`problem-card-${i}`}>
                                    <div className="w-10 h-10 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mb-4">
                                        <Icon className="w-5 h-5 text-rose-400" />
                                    </div>
                                    <h3 className={`text-base font-bold ${heading} mb-2`}>{p.title}</h3>
                                    <p className={`text-sm ${subhead} leading-relaxed`}>{p.desc}</p>
                                </motion.div>
                            );
                        })}
                    </motion.div>

                    <div className="text-center mt-12">
                        <p className={`text-lg ${heading} font-semibold`}>
                            Bunların hepsini <span className="text-[#C5A267]">tek bir ekranda</span> çözüyoruz.
                        </p>
                        <ChevronDown className={`w-6 h-6 mx-auto mt-3 ${muted} animate-bounce`} />
                    </div>
                </div>
            </section>

            {/* ═══════ FEATURES ═══════ */}
            <section className="py-24 px-4" data-testid="features-section">
                <div className="max-w-6xl mx-auto">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
                        className="text-center max-w-2xl mx-auto mb-14">
                        <motion.div variants={fadeUp} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C5A267]/10 border border-[#C5A267]/30 mb-4">
                            <Sparkles className="w-3 h-3 text-[#C5A267]" />
                            <span className="text-xs font-semibold text-[#C5A267]">NE İŞE YARAR</span>
                        </motion.div>
                        <motion.h2 variants={fadeUp} className={`text-3xl sm:text-4xl font-black ${heading} tracking-tight mb-4`}>
                            MacTech tam olarak ne işe yarar?
                        </motion.h2>
                        <motion.p variants={fadeUp} className={subhead}>
                            6 ayrı defteri tek ekranda topladık. İşte yaptıklarımız:
                        </motion.p>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {FEATURES.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <motion.div key={i} variants={fadeUp}
                                    className={`${cardBase} border rounded-2xl p-6 hover:border-[#C5A267]/40 hover:-translate-y-1 hover:shadow-[0_12px_32px_-16px_rgba(197,162,103,0.4)] transition-all group`}
                                    data-testid={`feature-card-${i}`}>
                                    <div className="w-12 h-12 rounded-xl bg-[#C5A267]/15 border border-[#C5A267]/30 flex items-center justify-center mb-5 group-hover:bg-[#C5A267]/25 transition-colors">
                                        <Icon className="w-6 h-6 text-[#C5A267]" />
                                    </div>
                                    <h3 className={`text-lg font-bold ${heading} mb-2`}>{f.title}</h3>
                                    <p className={`text-sm font-semibold text-[#C5A267]/90 mb-3 leading-snug`}>{f.head}</p>
                                    <p className={`text-sm ${subhead} leading-relaxed mb-4`}>{f.body}</p>
                                    <div className={`pt-3 border-t border-[#C5A267]/10`}>
                                        <p className={`text-xs ${muted} leading-relaxed`}>
                                            <span className="text-[#C5A267] font-semibold">Faydası:</span> {f.benefit}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* ═══════ SECTORS ═══════ */}
            <section className={`${sectionAlt} py-24 px-4 border-y border-[#C5A267]/10`} data-testid="sectors-section">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-10">
                        <h2 className={`text-3xl sm:text-4xl font-black ${heading} tracking-tight mb-3`}>
                            Şu meslekler bizi kullanıyor
                        </h2>
                        <p className={subhead}>Sektörünüze göre hazır ayarlarımız var. Sıfırdan başlamazsınız.</p>
                    </div>

                    {/* Tab pills — only ACTIVE sectors */}
                    <div className="flex flex-wrap justify-center gap-2 mb-8 px-1 overflow-x-auto">
                        {SECTORS.filter(s => s.active).map((s) => {
                            const Icon = s.icon;
                            const isActive = activeSector === s.key;
                            return (
                                <button key={s.key} onClick={() => setActiveSector(s.key)}
                                    className={`flex items-center gap-2 px-5 py-3 rounded-full border transition-all whitespace-nowrap
                                        ${isActive
                                            ? 'bg-[#C5A267] border-[#C5A267] text-black font-bold shadow-[0_4px_16px_-4px_rgba(197,162,103,0.5)]'
                                            : `${cardBase} ${subhead} hover:border-[#C5A267]/40`}`}
                                    data-testid={`sector-tab-${s.key}`}>
                                    <Icon className="w-4 h-4" />
                                    <span className="text-sm">{s.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Sector detail card */}
                    <motion.div key={activeSector} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                        className={`${cardBase} border-2 border-[#C5A267]/25 rounded-3xl p-6 sm:p-10 shadow-[0_20px_60px_-30px_rgba(197,162,103,0.3)]`}
                        data-testid={`sector-detail-${activeSector}`}>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div>
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="w-14 h-14 rounded-2xl bg-[#C5A267]/15 border border-[#C5A267]/35 flex items-center justify-center">
                                        <sector.icon className="w-7 h-7 text-[#C5A267]" />
                                    </div>
                                    <div>
                                        <div className={`text-xs uppercase tracking-wider ${muted}`}>Meslek</div>
                                        <h3 className={`text-2xl font-bold ${heading}`}>{sector.label}</h3>
                                    </div>
                                </div>
                                <div className={`p-4 rounded-xl bg-rose-500/5 border border-rose-500/20 mb-5`}>
                                    <p className={`text-xs uppercase tracking-wider text-rose-400 font-semibold mb-1.5`}>Sıkıntınız</p>
                                    <p className={`text-sm italic ${subhead} leading-relaxed`}>{sector.pain}</p>
                                </div>
                                <div className={`p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20`}>
                                    <p className={`text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-1.5`}>Sonuç</p>
                                    <p className={`text-sm ${heading} font-semibold leading-relaxed`}>{sector.result}</p>
                                </div>
                            </div>
                            <div>
                                <p className={`text-xs uppercase tracking-wider text-[#C5A267] font-semibold mb-4`}>MacTech&apos;in Çözümü</p>
                                <ul className="space-y-3 mb-6">
                                    {sector.solutions.map((s, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <div className="w-5 h-5 rounded-full bg-[#C5A267]/20 border border-[#C5A267]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <Check className="w-3 h-3 text-[#C5A267]" />
                                            </div>
                                            <span className={`text-sm ${subhead} leading-relaxed`}>{s}</span>
                                        </li>
                                    ))}
                                </ul>
                                <a href="#demo-form">
                                    <Button className="w-full bg-[#C5A267] hover:bg-[#B59257] text-black font-bold py-5 rounded-xl"
                                        data-testid={`sector-cta-${activeSector}`}>
                                        {sector.cta} <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    {/* Yakında diğer sektörler — küçük şerit */}
                    {SECTORS.some(s => !s.active) && (
                        <div className="mt-8 text-center">
                            <p className={`text-xs uppercase tracking-wider ${muted} mb-3`}>Yakında diğer meslekler için de</p>
                            <div className="flex flex-wrap items-center justify-center gap-4">
                                {SECTORS.filter(s => !s.active).map((s) => {
                                    const Icon = s.icon;
                                    return (
                                        <div key={s.key} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${cardBase} border opacity-60`}>
                                            <Icon className={`w-3.5 h-3.5 ${muted}`} />
                                            <span className={`text-xs ${muted}`}>{s.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <section className="py-24 px-4" data-testid="comparison-section">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-10 max-w-2xl mx-auto">
                        <h2 className={`text-3xl sm:text-4xl font-black ${heading} tracking-tight mb-3`}>
                            Şu an nasıl yönetiyorsunuz?
                        </h2>
                        <p className={subhead}>Eski yöntemle MacTech&apos;i karşılaştırın.</p>
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block">
                        <div className={`${cardBase} border rounded-2xl overflow-hidden shadow-xl`}>
                            <div className={`grid grid-cols-4 ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-100'} text-sm font-bold uppercase tracking-wider`}>
                                <div className={`p-4 ${muted}`}>Özellik</div>
                                <div className={`p-4 ${subhead} text-center`}>Defter / Excel</div>
                                <div className={`p-4 ${subhead} text-center`}>Yurt Dışı</div>
                                <div className="p-4 text-center bg-[#C5A267]/15 border-l border-r border-[#C5A267]/30 text-[#C5A267]">MacTech</div>
                            </div>
                            {COMPARISON.map((row, i) => (
                                <div key={i} className={`grid grid-cols-4 border-t ${isDark ? 'border-[#2a2a2a]' : 'border-gray-200'} text-sm`}>
                                    <div className={`p-4 font-semibold ${heading}`}>{row.label}</div>
                                    <div className={`p-4 flex items-center justify-center gap-2 ${subhead}`}>
                                        <CellIcon t={row.defter.t} /><span className="text-xs">{row.defter.v}</span>
                                    </div>
                                    <div className={`p-4 flex items-center justify-center gap-2 ${subhead}`}>
                                        <CellIcon t={row.abroad.t} /><span className="text-xs">{row.abroad.v}</span>
                                    </div>
                                    <div className={`p-4 flex items-center justify-center gap-2 bg-[#C5A267]/8 border-l border-r border-[#C5A267]/15`}>
                                        <CellIcon t={row.mactech.t} /><span className={`text-xs font-bold ${heading}`}>{row.mactech.v}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-4">
                        {COMPARISON.map((row, i) => (
                            <div key={i} className={`${cardBase} border rounded-xl p-4`}>
                                <h4 className={`text-sm font-bold ${heading} mb-3`}>{row.label}</h4>
                                <div className="space-y-2">
                                    <div className={`flex items-center justify-between text-xs ${subhead}`}>
                                        <span>Defter/Excel</span>
                                        <span className="flex items-center gap-1.5"><CellIcon t={row.defter.t} /> {row.defter.v}</span>
                                    </div>
                                    <div className={`flex items-center justify-between text-xs ${subhead}`}>
                                        <span>Yurt Dışı</span>
                                        <span className="flex items-center gap-1.5"><CellIcon t={row.abroad.t} /> {row.abroad.v}</span>
                                    </div>
                                    <div className={`flex items-center justify-between text-xs pt-2 border-t border-[#C5A267]/20`}>
                                        <span className="text-[#C5A267] font-semibold">MacTech</span>
                                        <span className={`flex items-center gap-1.5 font-bold ${heading}`}><CellIcon t={row.mactech.t} /> {row.mactech.v}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className={`text-center mt-8 ${heading} text-lg font-semibold`}>
                        Excel kötü değil. <span className="text-[#C5A267]">Sadece artık yetmiyor.</span>
                    </p>
                </div>
            </section>

            {/* ═══════ TRUST ═══════ */}
            <section className={`${sectionAlt} py-24 px-4 border-y border-[#C5A267]/10`} data-testid="trust-section">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12 max-w-2xl mx-auto">
                        <h2 className={`text-3xl sm:text-4xl font-black ${heading} tracking-tight mb-3`}>
                            Bilgileriniz emin ellerde
                        </h2>
                        <p className={subhead}>Bankalar gibi koruyoruz. Hatta yedeğini biz alıyoruz.</p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
                        {TRUST_ITEMS.map((t, i) => {
                            const Icon = t.icon;
                            return (
                                <div key={i} className={`${cardBase} border rounded-2xl p-6 text-center hover:border-[#C5A267]/35 transition-colors`}
                                    data-testid={`trust-card-${i}`}>
                                    <div className="w-12 h-12 mx-auto rounded-xl bg-[#C5A267]/15 border border-[#C5A267]/30 flex items-center justify-center mb-4">
                                        <Icon className="w-6 h-6 text-[#C5A267]" />
                                    </div>
                                    <h3 className={`text-base font-bold ${heading} mb-2`}>{t.title}</h3>
                                    <p className={`text-xs ${subhead} leading-relaxed`}>{t.desc}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Stats */}
                    <div className={`${cardBase} border-2 border-[#C5A267]/20 rounded-3xl p-8 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center`}>
                        {[
                            { v: '500+', l: 'Şu an kullanan işletme' },
                            { v: '%99.9', l: 'Sistemin açık kaldığı zaman' },
                            { v: '7/24', l: 'Türkçe destek hattımız' },
                        ].map((s, i) => (
                            <div key={i}>
                                <div className="text-4xl sm:text-5xl font-black text-[#C5A267] mb-2">{s.v}</div>
                                <div className={`text-sm ${subhead}`}>{s.l}</div>
                            </div>
                        ))}
                    </div>

                    {/* Reviews (real if available) */}
                    {reviewStats.total > 0 && (
                        <div className="mt-10 text-center">
                            <div className="inline-flex items-center gap-2">
                                {[1, 2, 3, 4, 5].map(n => (
                                    <Star key={n} className={`w-5 h-5 ${n <= Math.round(reviewStats.avg_rating) ? 'fill-[#C5A267] text-[#C5A267]' : muted}`} />
                                ))}
                                <span className={`font-bold ${heading} ml-2`}>{Number(reviewStats.avg_rating).toFixed(1)}</span>
                                <span className={muted}>· {reviewStats.total} yorum</span>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* ═══════ FAQ ═══════ */}
            <section className="py-24 px-4" data-testid="faq-section">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-10">
                        <h2 className={`text-3xl sm:text-4xl font-black ${heading} tracking-tight mb-3`}>
                            Sıkça merak edilenler
                        </h2>
                        <p className={subhead}>Sormak istediğiniz daha bir şey varsa <a href="tel:+905308487836" className="text-[#C5A267] underline">bizi arayın</a>.</p>
                    </div>

                    <div className="space-y-3">
                        {FAQ_ITEMS.map((item, i) => {
                            const isOpen = openFaq === i;
                            return (
                                <div key={i} className={`${cardBase} border rounded-xl overflow-hidden`} data-testid={`faq-item-${i}`}>
                                    <button onClick={() => setOpenFaq(isOpen ? -1 : i)}
                                        className="w-full px-5 py-4 flex items-center justify-between text-left gap-4"
                                        data-testid={`faq-toggle-${i}`}>
                                        <span className={`font-semibold ${heading}`}>{item.q}</span>
                                        <ChevronDown className={`w-5 h-5 text-[#C5A267] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isOpen && (
                                        <div className={`px-5 pb-5 ${subhead} leading-relaxed text-sm border-t ${isDark ? 'border-[#2a2a2a]' : 'border-gray-200'} pt-4`}>
                                            {item.a}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ═══════ DEMO FORM / FINAL CTA ═══════ */}
            <section id="demo-form" className={`${sectionAlt} py-24 px-4 border-t border-[#C5A267]/10`} data-testid="final-cta-section">
                <div className="max-w-5xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* LEFT: Pitch */}
                        <div>
                            <h2 className={`text-3xl sm:text-5xl font-black ${heading} tracking-tight leading-[1.1] mb-5`}>
                                Bugün <span className="text-[#C5A267]">defterleri kapatın</span>.
                            </h2>
                            <p className={`text-lg ${subhead} mb-8 leading-relaxed`}>
                                14 gün ücretsiz denersiniz. Beğenmezseniz hiçbir şey ödemezsiniz.
                            </p>

                            <ul className="space-y-3 mb-8">
                                {['14 gün tamamen ücretsiz', 'Kart bilgisi istemiyoruz', 'Hesap açar açmaz kullanmaya başlayın', 'Türkçe destek 7/24'].map(t => (
                                    <li key={t} className={`flex items-center gap-3 ${subhead}`}>
                                        <div className="w-5 h-5 rounded-full bg-[#C5A267]/20 border border-[#C5A267]/40 flex items-center justify-center flex-shrink-0">
                                            <Check className="w-3 h-3 text-[#C5A267]" />
                                        </div>
                                        <span className="text-sm">{t}</span>
                                    </li>
                                ))}
                            </ul>

                            {/* Store badges in final CTA */}
                            <div className="mb-6">
                                <p className={`text-xs uppercase tracking-wider ${muted} font-semibold mb-3`}>Telefonunuzdan da kullanın</p>
                                <StoreBadges size="md" testIdPrefix="cta-store" />
                            </div>

                            <div className={`${cardBase} border rounded-2xl p-5 space-y-3`}>
                                <p className={`text-xs uppercase tracking-wider ${muted} font-semibold`}>Hemen konuşmak isterseniz</p>
                                <a href="tel:+905308487836" className={`flex items-center gap-3 hover:text-[#C5A267] transition-colors ${heading}`} data-testid="cta-phone">
                                    <Phone className="w-5 h-5 text-[#C5A267]" />
                                    <span className="font-bold text-lg">0530 848 78 36</span>
                                </a>
                                <a href="https://wa.me/905308487836?text=Merhaba,%20MacTech%20hakkında%20bilgi%20almak%20istiyorum." target="_blank" rel="noopener noreferrer"
                                    className={`flex items-center gap-3 hover:text-[#C5A267] transition-colors ${subhead}`} data-testid="cta-whatsapp">
                                    <MessageCircle className="w-5 h-5 text-[#C5A267]" />
                                    <span className="text-sm">WhatsApp&apos;tan yazın</span>
                                </a>
                                <a href="mailto:info@mactech.tr" className={`flex items-center gap-3 hover:text-[#C5A267] transition-colors ${subhead}`} data-testid="cta-email">
                                    <Mail className="w-5 h-5 text-[#C5A267]" />
                                    <span className="text-sm">info@mactech.tr</span>
                                </a>
                            </div>
                        </div>

                        {/* RIGHT: Form */}
                        <div>
                            <DemoForm isDark={isDark} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Mobile sticky bottom bar */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 p-3 backdrop-blur-xl bg-black/80 border-t border-[#C5A267]/30 flex gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
                data-testid="mobile-sticky-cta">
                <a href="tel:+905308487836" className="flex-1">
                    <Button variant="outline" className="w-full border-[#C5A267]/50 text-[#C5A267] hover:bg-[#C5A267]/10 h-12">
                        <Phone className="w-4 h-4 mr-2" /> Ara
                    </Button>
                </a>
                <a href="#demo-form" className="flex-[2]">
                    <Button className="w-full bg-[#C5A267] hover:bg-[#B59257] text-black font-bold h-12">
                        Beni Arasınlar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </a>
            </div>
            {/* Spacer for sticky bar on mobile */}
            <div className="md:hidden h-20" />
        </div>
    );
};

export default Home;
export { Home };
